import { useState } from "react";
import { Link } from "react-router-dom";
import type { Product } from "@/types";
import { getPriceRange } from "@/types";
import { useCostMarkups, getCostMarkupsForCategories } from "@/contexts/CostMarkupContext";
import { useAuth } from "@/hooks/useAuth";
import { callFunction } from "@/utils/callFunction";

interface ProductCardProps {
  product: Product;
  onStatusChange?: (productId: string, newStatus: string) => void;
}

function normalizeCategories(raw: unknown): string[] {
  if (Array.isArray(raw)) return raw.filter((v) => typeof v === "string" && v.length > 0);
  if (raw && typeof raw === "object") {
    return Object.values(raw as Record<string, unknown>).filter(
      (v): v is string => typeof v === "string" && v.length > 0
    );
  }
  return [];
}

export default function ProductCard({ product, onStatusChange }: ProductCardProps) {
  const costMarkups = useCostMarkups();
  const { isAdmin } = useAuth();
  const [imgError, setImgError] = useState(false);
  const [toggling, setToggling] = useState(false);
  const categories = normalizeCategories(product.categories);
  const markups = getCostMarkupsForCategories(costMarkups, categories);

  const defaultCustomizationCost =
    product.customizationCost.silk_screen ??
    product.customizationCost.uv_print ??
    product.customizationCost.laser ??
    0;

  const supplierCost = product.variants[0]?.price ?? 0;
  const priceRange = getPriceRange(
    supplierCost,
    defaultCustomizationCost,
    product.pricingTiers,
    markups
  );

  const formatPrice = (value: number) =>
    new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);

  const handleToggleStatus = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (toggling) return;

    const newStatus = product.status === "active" ? "inactive" : "active";
    const label = newStatus === "inactive" ? "desativar" : "ativar";

    if (!window.confirm(`Deseja ${label} "${product.name}"?`)) return;

    setToggling(true);
    try {
      await callFunction("updateProductAdmin", {
        productId: product.id,
        updates: { status: newStatus },
      });
      onStatusChange?.(product.id, newStatus);
    } catch (err) {
      console.error("Erro ao alterar status:", err);
      alert("Erro ao alterar status. Tente novamente.");
    } finally {
      setToggling(false);
    }
  };

  const isInactive = product.status === "inactive";

  const saveScrollAndNavigate = () => {
    sessionStorage.setItem("catalog_scroll_position", String(window.scrollY));
  };

  return (
    <Link
      to={`/produto/${product.id}?from=catalog`}
      onClick={saveScrollAndNavigate}
      className={`group block rounded-xl border bg-bg transition-all duration-fast hover:border-border-hover hover:shadow-card-hover ${
        isInactive
          ? "border-dashed border-orange-300 opacity-60"
          : "border-border"
      }`}
    >
      <div className="relative aspect-square overflow-hidden rounded-t-xl bg-surface">
        {imgError || !product.imageUrl ? (
          <div className="flex h-full w-full items-center justify-center">
            <svg className="h-12 w-12 text-text-muted/30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z" />
            </svg>
          </div>
        ) : (
          <img
            src={product.imageUrl}
            alt={product.name}
            className="h-full w-full object-contain p-4 transition-transform duration-normal group-hover:scale-105"
            loading="lazy"
            onError={() => setImgError(true)}
          />
        )}

        {/* Badge admin: inativo */}
        {isInactive && (
          <span className="absolute left-2 top-2 z-10 rounded-full bg-orange-500 px-2 py-0.5 text-[10px] font-bold text-white shadow-md">
            Inativo
          </span>
        )}

        {/* Badge destaque */}
        {!isInactive && product.featured && (
          <span className="absolute left-2 top-2 flex items-center gap-1 rounded-full bg-gradient-to-r from-orange-500 to-red-500 px-2 py-0.5 text-[10px] font-bold text-white shadow-md">
            <svg className="h-3 w-3" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2L9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61z" />
            </svg>
            Destaque
          </span>
        )}

        {/* Botão admin: desativar/ativar */}
        {isAdmin && (
          <button
            type="button"
            onClick={handleToggleStatus}
            disabled={toggling}
            className={`absolute right-2 top-2 z-10 flex h-7 w-7 items-center justify-center rounded-full shadow-md transition-all disabled:opacity-50 ${
              isInactive
                ? "bg-emerald-500 text-white hover:bg-emerald-600"
                : "bg-red-500 text-white hover:bg-red-600"
            }`}
            title={isInactive ? "Ativar produto" : "Desativar produto"}
          >
            {toggling ? (
              <svg className="h-3.5 w-3.5 animate-spin" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            ) : isInactive ? (
              <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
              </svg>
            ) : (
              <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
              </svg>
            )}
          </button>
        )}
      </div>

      <div className="p-3 sm:p-4">
        <p className="mb-1 font-mono text-[10px] font-medium tracking-wider text-text-muted">
          {product.customSku || product.id}
        </p>

        {Array.isArray(product.categories) &&
          product.categories.length > 0 &&
          typeof product.categories[0] === "string" && (
            <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-accent">
              {product.categories[0]}
            </p>
          )}

        <h3 className="mb-2 text-sm font-semibold text-text-primary line-clamp-2 sm:text-base">
          {product.name}
        </h3>

        {priceRange ? (
          <div className="mt-2 rounded-lg bg-gradient-to-r from-emerald-50 to-teal-50 px-3 py-2">
            <p className="text-[10px] font-medium uppercase tracking-wide text-emerald-600">A partir de</p>
            <p className="text-lg font-extrabold leading-tight text-emerald-700">
              {formatPrice(priceRange.min)}
            </p>
            <div className="mt-1 flex items-center gap-1">
              <svg className="h-3 w-3 text-emerald-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path d="M12 20h9M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" />
              </svg>
              <p className="text-[9px] text-emerald-500">Valor sem Personalização</p>
            </div>
            <div className="mt-0.5 flex items-center gap-1">
              <svg className="h-3 w-3 text-emerald-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path d="M12 20h9M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" />
              </svg>
              <p className="text-[9px] text-emerald-500">Valores no Atacado</p>
            </div>
          </div>
        ) : (
          <p className="mt-2 text-sm text-text-muted">Consulte disponibilidade</p>
        )}

        <div className="mt-2.5 flex flex-wrap gap-1.5">
          {product.personalizationTypes.length > 0 && (
            <span className="flex items-center gap-1 rounded-full bg-surface px-2 py-0.5 text-[10px] text-text-secondary">
              <svg className="h-3 w-3 text-accent" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path d="M12 20h9M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" />
              </svg>
              {product.personalizationTypes.length} técnica{product.personalizationTypes.length > 1 ? "s" : ""}
            </span>
          )}
          {product.variants.length > 1 && (
            <span className="flex items-center gap-1 rounded-full bg-surface px-2 py-0.5 text-[10px] text-text-secondary">
              <svg className="h-3 w-3 text-accent" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <circle cx="12" cy="12" r="10" />
                <circle cx="12" cy="12" r="4" />
              </svg>
              {product.variants.length} cores
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}
